import type { ReactNode } from 'react'
import { getLang } from '../lib/lang'

export const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ')

export function Panel({
  title,
  subtitle,
  right,
  children,
  className,
}: {
  title?: ReactNode
  subtitle?: ReactNode
  right?: ReactNode
  children?: ReactNode
  className?: string
}) {
  return (
    <section className={cx('rounded-xl border border-slate-800 bg-slate-900/50 p-6 shadow-panel', className)}>
      {(title || right) && (
        <header className="mb-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            {title && <h3 className="text-[16px] font-semibold tracking-tight text-slate-50">{title}</h3>}
            {subtitle && <p className="mt-1 max-w-3xl text-[13px] leading-relaxed text-slate-500">{subtitle}</p>}
          </div>
          {right && <div className="shrink-0">{right}</div>}
        </header>
      )}
      {children}
    </section>
  )
}

/** A figure tile: big number, small uppercase label beneath. */
export function KpiCard({ label, value, foot, tone }: { label: ReactNode; value: ReactNode; foot?: ReactNode; tone?: 'pos' | 'neg' | 'warn' | undefined }) {
  const color = tone === 'pos' ? 'text-emerald-500' : tone === 'neg' ? 'text-rose-500' : tone === 'warn' ? 'text-amber-500' : 'text-sky-500'
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 px-5 py-4 shadow-panel">
      <div className={cx('text-[28px] font-extrabold leading-none tracking-tight tabular', color)}>{value}</div>
      <div className="label mt-2">{label}</div>
      {foot !== undefined && <div className="mt-1 text-[12px] text-slate-500">{foot}</div>}
    </div>
  )
}

export function ToggleChip({
  active,
  muted,
  onClick,
  children,
  title,
}: {
  active?: boolean | undefined
  muted?: boolean | undefined
  onClick?: (() => void) | undefined
  children: ReactNode
  title?: string | undefined
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cx(
        'rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition',
        muted
          ? 'border-dashed border-slate-700 text-slate-500 line-through'
          : active
            ? 'border-sky-500 bg-sky-500 text-white'
            : 'border-slate-800 bg-panel text-slate-300 hover:border-slate-600 hover:text-slate-100',
      )}
    >
      {children}
    </button>
  )
}

export type TagTone = 'pos' | 'neg' | 'warn' | 'info' | 'neutral'

export function Tag({ tone = 'neutral', children }: { tone?: TagTone | undefined; children: ReactNode }) {
  const cls = {
    pos: 'bg-emerald-500/10 text-emerald-500 ring-emerald-500/20',
    neg: 'bg-rose-500/10 text-rose-500 ring-rose-500/20',
    warn: 'bg-amber-500/10 text-amber-500 ring-amber-500/20',
    info: 'bg-sky-500/10 text-sky-500 ring-sky-500/20',
    neutral: 'bg-slate-900 text-slate-400 ring-slate-800',
  }[tone]
  return <span className={cx('inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.06em] ring-1 ring-inset', cls)}>{children}</span>
}

export interface Column<T> {
  key: string
  header: ReactNode
  align?: 'left' | 'right'
  render: (row: T) => ReactNode
  className?: string
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  maxHeight,
}: {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  empty?: ReactNode
  maxHeight?: string
}) {
  const emptyText = empty ?? (getLang() === 'de' ? 'Nichts vorhanden.' : 'Nothing here.')
  return (
    <div className="-mx-6 overflow-auto px-6" style={maxHeight ? { maxHeight } : undefined}>
      <table className="w-full text-[14px]">
        <thead className="sticky top-0 z-10 bg-slate-900/95">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={cx('label border-b border-slate-800 px-3 py-3 font-medium', c.align === 'right' ? 'text-right' : 'text-left')}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-10 text-center text-[13px] text-slate-500">
                {emptyText}
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr
              key={rowKey(r)}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              className={cx('border-b border-slate-800/80 transition-colors', onRowClick && 'cursor-pointer hover:bg-slate-900/40')}
            >
              {columns.map((c) => (
                <td key={c.key} className={cx('whitespace-nowrap px-3 py-3 align-middle tabular text-slate-300', c.align === 'right' ? 'text-right' : 'text-left', c.className)}>
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function MeterBar({ pct, accent = 'rgb(var(--accent))' }: { pct: number; accent?: string }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-900">
      <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: accent }} />
    </div>
  )
}

/** Signed values use tone, never a plus sign alone. `goodIsNegative` inverts the good direction. */
export function Signed({ value, children, goodIsNegative = false, className }: { value: number; children: ReactNode; goodIsNegative?: boolean; className?: string }) {
  const good = goodIsNegative ? value < 0 : value > 0
  const bad = goodIsNegative ? value > 0 : value < 0
  return <span className={cx('tabular', good ? 'text-emerald-500' : bad ? 'text-rose-500' : 'text-slate-300', className)}>{children}</span>
}

export function Field({ label, children, hint }: { label: ReactNode; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1.5 text-[12px] leading-relaxed text-slate-500">{hint}</p>}
    </label>
  )
}

export function ErrorBox({ children }: { children: ReactNode }) {
  return <div className="rounded-md border border-rose-500/30 bg-rose-500/5 px-4 py-3 text-[14px] text-rose-500">{children}</div>
}

export function WarnBox({ children }: { children: ReactNode }) {
  return <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-[14px] text-amber-500">{children}</div>
}

export function InfoBox({ children }: { children: ReactNode }) {
  return <div className="rounded-md border border-sky-500/25 bg-sky-500/5 px-4 py-3 text-[14px] text-sky-500">{children}</div>
}

export function Spinner({ label }: { label?: string }) {
  return <div className="py-12 text-center text-[13px] text-slate-500">{label ?? (getLang() === 'de' ? 'Wird geladen…' : 'Loading…')}</div>
}

/** The navy hero surface: gradient, compass rings, white type. Every screen opens with one. */
export const HERO_NAVY = '#12355b'
export const HERO_GOLD = '#d6a84e'
export function HeroSurface({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cx('relative overflow-hidden text-white', className)}
      style={{ background: `linear-gradient(160deg, #24466f 0%, ${HERO_NAVY} 55%, #172f4e 100%)` }}
    >
      <div className="pointer-events-none absolute -right-16 -top-28 h-[320px] w-[320px] rounded-full border border-white/[0.06]" />
      <div className="relative">{children}</div>
    </div>
  )
}

/** A small fact: label, value, optional note. */
export function Chip({ label, value, note, tone }: { label: ReactNode; value: ReactNode; note?: ReactNode; tone?: 'pos' | 'neg' | 'warn' | undefined }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-800 bg-panel px-2.5 py-1 text-[12px]">
      <span className="text-slate-500">{label}</span>
      <b className={cx('tabular', tone === 'pos' ? 'text-emerald-500' : tone === 'neg' ? 'text-rose-500' : tone === 'warn' ? 'text-amber-500' : 'text-slate-200')}>{value}</b>
      {note !== undefined && <span className="text-slate-500">{note}</span>}
    </span>
  )
}

export interface HeroStatProps {
  label: ReactNode
  value: ReactNode
  foot?: ReactNode
  tone?: 'pos' | 'neg' | 'warn' | undefined
}

/** Every screen opens the same way: a serif title, one line of context, actions, and the figures that matter as tiles. */
export function ScreenHeading({ title, subtitle, right, stats }: { title: ReactNode; subtitle?: ReactNode; right?: ReactNode; stats?: HeroStatProps[] | undefined }) {
  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
        <h2 className="serif min-w-0 text-[26px] font-bold leading-tight text-sky-500">
          {title}
          {subtitle && <span className="ml-2 font-sans text-[14px] font-normal text-slate-500">— {subtitle}</span>}
        </h2>
        {right && <div className="shrink-0">{right}</div>}
      </div>
      {stats && stats.length > 0 && (
        <div className="mt-5 grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
          {stats.map((st, i) => (
            <KpiCard key={i} label={st.label} value={st.value} foot={st.foot} tone={st.tone} />
          ))}
        </div>
      )}
    </div>
  )
}
